import { Component, Input, OnChanges, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AccountGroup } from '@galaxy/account-group';
import { Environment, EnvironmentService } from '@galaxy/core';
import { PersonaType } from '@vendasta/iam';
import { ProjectsAndSubtasksByProductsService, ProjectTrackerInfo, SidenavService } from '@vendasta/project-tracker';
import { ConfigInterface, Order, SalesOrdersService, UserInterface as User } from '@vendasta/sales-orders';
import { SalesOrderConfirmationComponent, SalesOrderContentsComponent } from '@vendasta/sales-ui';
import { OrderFormOptionsInterface } from '@vendasta/store';
import { Observable, ReplaySubject, Subscription, forkJoin, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { DEMO_SSC_UPLOAD_URL, PROD_SSC_UPLOAD_URL } from '../../shared/constants';
import { HistoryComponent } from '../history/history.component';
import { OrderFormComponent } from '../order-form/order-form.component';
import { WorkOrderDetailsHelperService } from './work-order-details-helper.service';
import {
  WorkOrderFormService,
  WorkOrderPersona,
  WorkOrderSalesOrderSummaryInfo,
  WorkOrderSalesOrderSummaryService,
} from '@vendasta/work-order';
import { FulfillmentOrder, FulfillmentOrderStatus } from '@vendasta/order-fulfillment';

@Component({
  selector: 'orders-view-order',
  templateUrl: './view-order.component.html',
  styleUrls: [
    '../../shared/shared-styles.scss',
    '../create-order/create/create-order.component.scss',
    './view-order.component.scss',
  ],
})
export class ViewOrderComponent implements OnInit, OnDestroy, OnChanges {
  @Input() business: Pick<AccountGroup, 'externalIdentifiers' | 'napData' | 'accountGroupId'>;
  @Input() order: Order;
  @Input() users: User[];
  @Input() productIds: string[];
  @Input() fileUploadUrl: string;
  @Input() showFulfillmentTrackers = false;
  @Input() hideNotesSections?: boolean;
  @Input() hideTagsSection?: boolean;
  @Input() hideAttachmentsSection?: boolean;
  @Input() workOrderPersona?: WorkOrderPersona = WorkOrderPersona.SALES;

  orderFormOptions$: Observable<OrderFormOptionsInterface>;
  orderConfig$: Observable<ConfigInterface>;
  order$$ = new ReplaySubject<Order>(1);
  order$ = this.order$$.asObservable();

  orderFormGroup: UntypedFormGroup;
  orderDetailsFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  subscriptions: Subscription[] = [];
  attachmentsUploadUrl = PROD_SSC_UPLOAD_URL;

  @ViewChild(HistoryComponent) statusHistoryComponent: HistoryComponent;
  @ViewChild(SalesOrderContentsComponent) contentComponent: SalesOrderContentsComponent;
  @ViewChild(SalesOrderConfirmationComponent) confirmationComponent: SalesOrderConfirmationComponent;
  @ViewChild('orderForm') readonly salesOrderForm: OrderFormComponent;

  private expanded$$ = new ReplaySubject<boolean>(1);
  expanded$ = this.expanded$$.asObservable();

  projectTrackerInfo$: Observable<ProjectTrackerInfo[]>;
  selectedProjectTrackerInfo: ProjectTrackerInfo;
  showFulfillmentsCard$: Observable<boolean>;
  @ViewChild('commentsContent') commentsContent: TemplateRef<any>;
  workOrderInfo$: Observable<WorkOrderSalesOrderSummaryInfo[]>;
  shouldShowDetailsNeededBanner$: Observable<boolean>;

  constructor(
    private environmentService: EnvironmentService,
    private salesOrdersService: SalesOrdersService,
    private formBuilder: UntypedFormBuilder,
    private projectTrackerService: ProjectsAndSubtasksByProductsService,
    private sidenavService: SidenavService,
    private workOrderDetailsHelperService: WorkOrderDetailsHelperService,
    private workOrderFormService: WorkOrderFormService,
    private workOrderSalesOrderSummaryService: WorkOrderSalesOrderSummaryService,
  ) {
    this.orderFormGroup = this.formBuilder.group({});
  }

  ngOnInit(): void {
    if (this.environmentService.getEnvironment() === Environment.DEMO) {
      this.attachmentsUploadUrl = DEMO_SSC_UPLOAD_URL;
    }

    this.orderConfig$ = this.salesOrdersService
      .getConfig(this.business.externalIdentifiers.partnerId, this.business.externalIdentifiers.marketId)
      .pipe(shareReplay(1));

    this.orderFormOptions$ = this.orderConfig$.pipe(
      map((config: ConfigInterface) => {
        return {
          readOnly: true,
          showOfficeUseQuestions: true,
          bypassRequiredQuestions: !config.salespersonOptions.validateRequiredFields,
        };
      }),
    );

    this.projectTrackerInfo$ = this.projectTrackerService
      .lookupVisibleProjectsForAccountByOrderIds(
        this.business.externalIdentifiers.partnerId,
        this.business.accountGroupId,
        this.order?.orderId ? [this.order.orderId] : [],
        [PersonaType.sales_person, PersonaType.partner, PersonaType.smb],
      )
      .pipe(
        map((info: ProjectTrackerInfo[]) =>
          info.map((tracker) => {
            return {
              ...tracker,
              subtasks: tracker.subtasks,
            };
          }),
        ),
      );

    const workOrdersForSalesOrder$: Observable<FulfillmentOrder[]> = this.order$.pipe(
      switchMap((salesOrder) => {
        if (salesOrder) {
          return this.workOrderSalesOrderSummaryService.getWorkOrdersForSalesOrder(
            salesOrder.orderId,
            salesOrder.businessId,
          );
        }
        return of([]);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const workOrdersHasForms$: Observable<boolean[]> = workOrdersForSalesOrder$.pipe(
      switchMap((workOrders) => {
        const doesWorkOrderHaveForms: Observable<boolean>[] = workOrders.map((wo) =>
          this.workOrderFormService.doesWorkOrderHaveForm(wo.appId),
        );
        return forkJoin(doesWorkOrderHaveForms);
      }),
    );

    this.showFulfillmentsCard$ = workOrdersHasForms$.pipe(
      map((hasFormArray) => hasFormArray.some((hasForm) => !!hasForm)),
    );

    this.shouldShowDetailsNeededBanner$ = workOrdersForSalesOrder$.pipe(
      map((workOrders) =>
        workOrders.some((wo) => wo.status === FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_DETAILS_NEEDED),
      ),
    );

    this.workOrderInfo$ = this.workOrderDetailsHelperService.getWorkOrdersForSalesOrder(
      this.order,
      workOrdersHasForms$,
      workOrdersForSalesOrder$,
    );
  }

  ngOnChanges(): void {
    this.order$$.next(this.order);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
  }

  // called by parent components for print-friendly display of the order contents
  public openDropdowns(): void {
    this.expanded$$.next(true);
    this.statusHistoryComponent.openDropdowns();
    this.contentComponent.signalToOpenAllDropdowns();
    this.confirmationComponent.openDropdown();
    this.salesOrderForm.openDropdown();
  }

  handleAskQuestionClicked(projectTrackerInfo: ProjectTrackerInfo): void {
    this.selectedProjectTrackerInfo = projectTrackerInfo;
    this.sidenavService.open(this.commentsContent);
  }
}
